/**
 * CRM API helper. Все вызовы используют JWT из AuthContext и базовый
 * URL backend через REACT_APP_BACKEND_URL. Возвращает Promise с data.
 */
import axios from "axios";
import { getApiBaseUrl, getBackendOrigin, getCrmApiBaseUrl } from "./apiBase";

const API = getApiBaseUrl();
const CRM = getCrmApiBaseUrl();
const CRM_PUBLIC = `${CRM}/public`;

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
});

export const crmApi = {
  // ----- clients -----
  listClients: (token, params) =>
    axios
      .get(`${CRM}/clients`, { params, headers: authHeaders(token) })
      .then((r) => r.data),
  getClient: (token, id) =>
    axios.get(`${CRM}/clients/${id}`, { headers: authHeaders(token) }).then((r) => r.data),
  createClient: (token, body) =>
    axios.post(`${CRM}/clients`, body, { headers: authHeaders(token) }).then((r) => r.data),
  patchClient: (token, id, body) =>
    axios.patch(`${CRM}/clients/${id}`, body, { headers: authHeaders(token) }).then((r) => r.data),
  getClientActivity: (token, clientId, params) =>
    axios
      .get(`${CRM}/clients/${clientId}/activity`, { params, headers: authHeaders(token) })
      .then((r) => r.data),

  // ----- messages -----
  listMessages: (token, clientId, params) =>
    axios
      .get(`${CRM}/clients/${clientId}/messages`, { params, headers: authHeaders(token) })
      .then((r) => r.data),
  getMessageMediaBlob: (token, messageId) =>
    axios
      .get(`${CRM}/messages/${messageId}/media`, {
        headers: authHeaders(token),
        responseType: "blob",
      })
      .then((r) => r.data),

  // ----- requests -----
  listRequests: (token, clientId) =>
    axios.get(`${CRM}/clients/${clientId}/requests`, { headers: authHeaders(token) }).then((r) => r.data),
  createRequest: (token, clientId, body) =>
    axios.post(`${CRM}/clients/${clientId}/requests`, body, { headers: authHeaders(token) }).then((r) => r.data),
  patchRequest: (token, id, body) =>
    axios.patch(`${CRM}/requests/${id}`, body, { headers: authHeaders(token) }).then((r) => r.data),
  deleteRequest: (token, id) =>
    axios.delete(`${CRM}/requests/${id}`, { headers: authHeaders(token) }),

  // ----- stock availability subscriptions -----
  listStockSubscriptions: (token, clientId, params) =>
    axios
      .get(`${CRM}/clients/${clientId}/stock-subscriptions`, {
        params,
        headers: authHeaders(token),
      })
      .then((r) => r.data),
  listAllStockSubscriptions: (token, params) =>
    axios
      .get(`${CRM}/stock-subscriptions`, { params, headers: authHeaders(token) })
      .then((r) => r.data),
  createStockSubscription: (token, body) =>
    axios
      .post(`${CRM}/stock-subscriptions`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  cancelStockSubscription: (token, id) =>
    axios
      .delete(`${CRM}/stock-subscriptions/${id}`, { headers: authHeaders(token) })
      .then((r) => r.data),

  // ----- tags -----
  listTags: (token, clientId) =>
    axios.get(`${CRM}/clients/${clientId}/tags`, { headers: authHeaders(token) }).then((r) => r.data),
  createTag: (token, clientId, body) =>
    axios.post(`${CRM}/clients/${clientId}/tags`, body, { headers: authHeaders(token) }).then((r) => r.data),
  patchTag: (token, id, body) =>
    axios.patch(`${CRM}/tags/${id}`, body, { headers: authHeaders(token) }).then((r) => r.data),
  deleteTag: (token, id) =>
    axios.delete(`${CRM}/tags/${id}`, { headers: authHeaders(token) }),

  // ----- vehicles -----
  listVehicles: (token, clientId) =>
    axios.get(`${CRM}/clients/${clientId}/vehicles`, { headers: authHeaders(token) }).then((r) => r.data),
  createVehicle: (token, clientId, body) =>
    axios.post(`${CRM}/clients/${clientId}/vehicles`, body, { headers: authHeaders(token) }).then((r) => r.data),
  patchVehicle: (token, id, body) =>
    axios.patch(`${CRM}/vehicles/${id}`, body, { headers: authHeaders(token) }).then((r) => r.data),
  makePrimary: (token, id) =>
    axios.post(`${CRM}/vehicles/${id}/make-primary`, null, { headers: authHeaders(token) }).then((r) => r.data),
  deleteVehicle: (token, id) =>
    axios.delete(`${CRM}/vehicles/${id}`, { headers: authHeaders(token) }),

  // ----- deals -----
  listDeals: (token, clientId) =>
    axios.get(`${CRM}/clients/${clientId}/deals`, { headers: authHeaders(token) }).then((r) => r.data),
  createDeal: (token, clientId, body) =>
    axios.post(`${CRM}/clients/${clientId}/deals`, body, { headers: authHeaders(token) }).then((r) => r.data),
  patchDeal: (token, id, body) =>
    axios.patch(`${CRM}/deals/${id}`, body, { headers: authHeaders(token) }).then((r) => r.data),
  deleteDeal: (token, id) =>
    axios.delete(`${CRM}/deals/${id}`, { headers: authHeaders(token) }),

  // ----- customer orders -----
  listCustomerOrders: (token, params) =>
    axios.get(`${CRM}/customer-orders`, { params, headers: authHeaders(token) }).then((r) => r.data),
  listCustomerOrdersByClient: (token, clientId) =>
    axios
      .get(`${CRM}/clients/${clientId}/customer-orders`, { headers: authHeaders(token) })
      .then((r) => r.data),
  getCustomerOrder: (token, id) =>
    axios.get(`${CRM}/customer-orders/${id}`, { headers: authHeaders(token) }).then((r) => r.data),
  createCustomerOrder: (token, body) =>
    axios.post(`${CRM}/customer-orders`, body, { headers: authHeaders(token) }).then((r) => r.data),
  patchCustomerOrder: (token, id, body) =>
    axios.patch(`${CRM}/customer-orders/${id}`, body, { headers: authHeaders(token) }).then((r) => r.data),
  changeCustomerOrderStatus: (token, id, status) =>
    axios
      .post(`${CRM}/customer-orders/${id}/change-status`, { status }, { headers: authHeaders(token) })
      .then((r) => r.data),
  addCustomerOrderItem: (token, id, body) =>
    axios.post(`${CRM}/customer-orders/${id}/items`, body, { headers: authHeaders(token) }).then((r) => r.data),
  patchCustomerOrderItem: (token, orderId, itemId, body) =>
    axios
      .patch(`${CRM}/customer-orders/${orderId}/items/${itemId}`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  deleteCustomerOrderItem: (token, orderId, itemId) =>
    axios.delete(`${CRM}/customer-orders/${orderId}/items/${itemId}`, { headers: authHeaders(token) }),
  createCustomerOrderPublicLink: (token, id) =>
    axios.post(`${CRM}/customer-orders/${id}/public-link`, null, { headers: authHeaders(token) }).then((r) => r.data),
  sendCustomerOrderToClient: (token, id) =>
    axios.post(`${CRM}/customer-orders/${id}/send-to-client`, null, { headers: authHeaders(token) }).then((r) => r.data),
  refreshCustomerOrderPrices: (token, id) =>
    axios.post(`${CRM}/customer-orders/${id}/refresh-prices`, null, { headers: authHeaders(token) }).then((r) => r.data),
  openCustomerOrderPrint: async (token, orderId) => {
    const response = await axios.get(`${CRM}/customer-orders/${orderId}/print`, {
      headers: authHeaders(token),
      params: { auto_print: true },
      responseType: "text",
    });
    const blob = new Blob([response.data], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      URL.revokeObjectURL(url);
      throw new Error("Не удалось открыть окно печати");
    }
    win.addEventListener("load", () => URL.revokeObjectURL(url), { once: true });
  },
  downloadCustomerOrderPdf: async (token, orderId, orderNumber) => {
    const response = await axios.get(`${CRM}/customer-orders/${orderId}/pdf`, {
      headers: authHeaders(token),
      responseType: "blob",
    });
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Zakaz-${orderNumber || orderId}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
  recordCustomerOrderSupplierCart: (token, id, body = {}) =>
    axios
      .post(`${CRM}/customer-orders/${id}/supplier-cart`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  getCustomerOrderAnalytics: (token) =>
    axios
      .get(`${CRM}/analytics/customer-orders`, { headers: authHeaders(token) })
      .then((r) => r.data),
  getProductProfitOrderLines: (token, params) =>
    axios
      .get(`${CRM}/analytics/customer-orders/product-lines`, { params, headers: authHeaders(token) })
      .then((r) => r.data),
  getProductSalesHistory: (token, params) =>
    axios
      .get(`${CRM}/analytics/customer-orders/product-sales-history`, {
        params,
        headers: authHeaders(token),
      })
      .then((r) => r.data),
  getClientProfitOrderLines: (token, clientId) =>
    axios
      .get(`${CRM}/analytics/customer-orders/client-lines`, {
        params: { client_id: clientId },
        headers: authHeaders(token),
      })
      .then((r) => r.data),
  onecHealth: (token) =>
    axios.get(`${CRM}/onec/health`, { headers: authHeaders(token) }).then((r) => r.data),
  onecPreviewOrder: (token, orderId) =>
    axios
      .get(`${CRM}/customer-orders/${orderId}/onec-preview`, { headers: authHeaders(token) })
      .then((r) => r.data),
  onecSyncOrder: (token, orderId) =>
    axios
      .post(`${CRM}/customer-orders/${orderId}/onec-sync`, null, { headers: authHeaders(token) })
      .then((r) => r.data),
  onecSyncPayment: (token, paymentId) =>
    axios
      .post(`${CRM}/customer-payments/${paymentId}/onec-sync`, null, { headers: authHeaders(token) })
      .then((r) => r.data),
  getPublicCustomerOrder: (publicToken) =>
    axios.get(`${CRM_PUBLIC}/customer-orders/${publicToken}`).then((r) => r.data),
  confirmPublicCustomerOrder: (publicToken) =>
    axios.post(`${CRM_PUBLIC}/customer-orders/${publicToken}/confirm`).then((r) => r.data),
  declinePublicCustomerOrder: (publicToken, body = {}) =>
    axios.post(`${CRM_PUBLIC}/customer-orders/${publicToken}/decline`, body).then((r) => r.data),
  requestPublicCustomerOrderPrice: (publicToken, body = {}) =>
    axios.post(`${CRM_PUBLIC}/customer-orders/${publicToken}/request-price`, body).then((r) => r.data),

  // ----- customer payments -----
  listCustomerOrderPayments: (token, orderId) =>
    axios
      .get(`${CRM}/customer-orders/${orderId}/payments`, { headers: authHeaders(token) })
      .then((r) => r.data),
  createCustomerOrderPayment: (token, orderId, body) =>
    axios
      .post(`${CRM}/customer-orders/${orderId}/payments`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  getCustomerPayment: (token, paymentId) =>
    axios.get(`${CRM}/customer-payments/${paymentId}`, { headers: authHeaders(token) }).then((r) => r.data),
  patchCustomerPayment: (token, paymentId, body) =>
    axios
      .patch(`${CRM}/customer-payments/${paymentId}`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  cancelCustomerPayment: (token, paymentId) =>
    axios
      .post(`${CRM}/customer-payments/${paymentId}/cancel`, null, { headers: authHeaders(token) })
      .then((r) => r.data),
  refundCustomerPayment: (token, paymentId) =>
    axios
      .post(`${CRM}/customer-payments/${paymentId}/refund`, null, { headers: authHeaders(token) })
      .then((r) => r.data),
  createKaspiInvoice: (token, orderId, body = {}) =>
    axios
      .post(`${CRM}/customer-orders/${orderId}/payments/kaspi-invoice`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  createKaspiQr: (token, orderId, body = {}) =>
    axios
      .post(`${CRM}/customer-orders/${orderId}/payments/kaspi-qr`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  cancelKaspiPayment: (token, paymentId) =>
    axios
      .post(`${CRM}/customer-payments/${paymentId}/kaspi-cancel`, null, { headers: authHeaders(token) })
      .then((r) => r.data),
  refundKaspiPayment: (token, paymentId) =>
    axios
      .post(`${CRM}/customer-payments/${paymentId}/kaspi-refund`, null, { headers: authHeaders(token) })
      .then((r) => r.data),
  getKaspiHealth: (token) =>
    axios.get(`${CRM}/kaspi/health`, { headers: authHeaders(token) }).then((r) => r.data),
  getKaspiDashboardSettings: (token) =>
    axios.get(`${CRM}/kaspi/dashboard/settings`, { headers: authHeaders(token) }).then((r) => r.data),
  getKaspiDashboardStats: (token, day) =>
    axios
      .get(`${CRM}/kaspi/dashboard/stats`, {
        headers: authHeaders(token),
        params: day ? { day } : undefined,
      })
      .then((r) => r.data),
  getKaspiDashboardTransactions: (token, { day, limit } = {}) =>
    axios
      .get(`${CRM}/kaspi/dashboard/transactions`, {
        headers: authHeaders(token),
        params: { ...(day ? { day } : {}), ...(limit ? { limit } : {}) },
      })
      .then((r) => r.data),
  getKaspiLogs: (token, { day, limit } = {}) =>
    axios
      .get(`${CRM}/kaspi/logs`, {
        headers: authHeaders(token),
        params: { ...(day ? { day } : {}), ...(limit ? { limit } : {}) },
      })
      .then((r) => r.data),
  getKaspiTransactionDetails: (token, operationId, { source, paymentMethod, operationMethod } = {}) =>
    axios
      .get(`${CRM}/kaspi/transactions/${encodeURIComponent(operationId)}/details`, {
        headers: authHeaders(token),
        params: {
          ...(source ? { source } : {}),
          ...(paymentMethod ? { payment_method: paymentMethod } : {}),
          ...(operationMethod != null ? { operation_method: operationMethod } : {}),
        },
      })
      .then((r) => r.data),
  refundKaspiTransaction: (token, operationId, body) =>
    axios
      .post(`${CRM}/kaspi/transactions/${encodeURIComponent(operationId)}/refund`, body, {
        headers: authHeaders(token),
      })
      .then((r) => r.data),
  cancelKaspiTransaction: (token, operationId, body = {}) =>
    axios
      .post(`${CRM}/kaspi/transactions/${encodeURIComponent(operationId)}/cancel`, body, {
        headers: authHeaders(token),
      })
      .then((r) => r.data),
  kaspiAuthInit: (token) =>
    axios.post(`${CRM}/kaspi/auth/init`, null, { headers: authHeaders(token) }).then((r) => r.data),
  kaspiAuthSendPhone: (token, body) =>
    axios.post(`${CRM}/kaspi/auth/send-phone`, body, { headers: authHeaders(token) }).then((r) => r.data),
  kaspiAuthVerifyOtp: (token, body) =>
    axios.post(`${CRM}/kaspi/auth/verify-otp`, body, { headers: authHeaders(token) }).then((r) => r.data),
  kaspiSessionCheck: (token) =>
    axios.post(`${CRM}/kaspi/session/check`, null, { headers: authHeaders(token) }).then((r) => r.data),
  kaspiTestInvoice: (token, body) =>
    axios.post(`${CRM}/kaspi/test/invoice`, body, { headers: authHeaders(token) }).then((r) => r.data),
  kaspiTestInvoiceItems: (token, body) =>
    axios.post(`${CRM}/kaspi/test/invoice-items`, body, { headers: authHeaders(token) }).then((r) => r.data),
  kaspiTestQr: (token, body) =>
    axios.post(`${CRM}/kaspi/test/qr`, body, { headers: authHeaders(token) }).then((r) => r.data),
  kaspiTestQrCart: (token, body) =>
    axios.post(`${CRM}/kaspi/test/qr-cart`, body, { headers: authHeaders(token) }).then((r) => r.data),
  kaspiTestCancel: (token, body) =>
    axios.post(`${CRM}/kaspi/test/cancel`, body, { headers: authHeaders(token) }).then((r) => r.data),
  kaspiGetOperationStatus: (token, operationId, paymentType = "invoice") =>
    axios
      .get(`${CRM}/kaspi/operations/${encodeURIComponent(operationId)}`, {
        headers: authHeaders(token),
        params: { payment_type: paymentType },
      })
      .then((r) => r.data),
  syncKaspiPayment: (token, paymentId) =>
    axios
      .post(`${CRM}/customer-payments/${paymentId}/kaspi-sync`, null, { headers: authHeaders(token) })
      .then((r) => r.data),
  sendKaspiPaymentWhatsApp: (token, paymentId) =>
    axios
      .post(`${CRM}/customer-payments/${paymentId}/kaspi-send-whatsapp`, null, { headers: authHeaders(token) })
      .then((r) => r.data),
  listClientPayments: (token, clientId) =>
    axios.get(`${CRM}/clients/${clientId}/payments`, { headers: authHeaders(token) }).then((r) => r.data),

  // ----- price catalog (same base URL as HomePageNew) -----
  listProducts: (params) => axios.get(`${API}/products`, { params }).then((r) => r.data),

  sendMessage: (token, clientId, text) =>
    axios
      .post(`${CRM}/clients/${clientId}/messages/send`, { text }, { headers: authHeaders(token) })
      .then((r) => r.data),

  // ----- health -----
  health: () => axios.get(`${CRM}/health`).then((r) => r.data),

  // ----- automation settings -----
  getAutoReplySettings: (token) =>
    axios.get(`${CRM}/settings/auto-reply`, { headers: authHeaders(token) }).then((r) => r.data),
  patchAutoReplySettings: (token, body) =>
    axios.patch(`${CRM}/settings/auto-reply`, body, { headers: authHeaders(token) }).then((r) => r.data),
  listWelcomeTemplates: (token) =>
    axios.get(`${CRM}/settings/welcome-templates`, { headers: authHeaders(token) }).then((r) => r.data),
  createWelcomeTemplate: (token, body) =>
    axios.post(`${CRM}/settings/welcome-templates`, body, { headers: authHeaders(token) }).then((r) => r.data),
  patchWelcomeTemplate: (token, id, body) =>
    axios.patch(`${CRM}/settings/welcome-templates/${id}`, body, { headers: authHeaders(token) }).then((r) => r.data),
  deleteWelcomeTemplate: (token, id) =>
    axios.delete(`${CRM}/settings/welcome-templates/${id}`, { headers: authHeaders(token) }),
  getProfileSurveySettings: (token) =>
    axios.get(`${CRM}/settings/profile-survey`, { headers: authHeaders(token) }).then((r) => r.data),
  patchProfileSurveySettings: (token, body) =>
    axios.patch(`${CRM}/settings/profile-survey`, body, { headers: authHeaders(token) }).then((r) => r.data),
  getTelegramSettings: (token) =>
    axios.get(`${CRM}/settings/telegram`, { headers: authHeaders(token) }).then((r) => r.data),
  patchTelegramSettings: (token, body) =>
    axios.patch(`${CRM}/settings/telegram`, body, { headers: authHeaders(token) }).then((r) => r.data),
  getDiagnostics: (token) =>
    axios.get(`${CRM}/settings/diagnostics`, { headers: authHeaders(token) }).then((r) => r.data),
  getGreenApiSettings: (token) =>
    axios.get(`${CRM}/settings/green-api`, { headers: authHeaders(token) }).then((r) => r.data),
  patchGreenApiSettings: (token, body) =>
    axios.patch(`${CRM}/settings/green-api`, body, { headers: authHeaders(token) }).then((r) => r.data),

  // ----- automation actions -----
  sendSurvey: (token, clientId) =>
    axios.post(`${CRM}/clients/${clientId}/automation/send-survey`, null, { headers: authHeaders(token) }).then((r) => r.data),
  resetSurvey: (token, clientId, body = {}) =>
    axios.post(`${CRM}/clients/${clientId}/automation/reset-survey`, body, { headers: authHeaders(token) }).then((r) => r.data),
  debugParseSurvey: (token, clientId, body) =>
    axios.post(`${CRM}/clients/${clientId}/automation/debug-parse-survey`, body, { headers: authHeaders(token) }).then((r) => r.data),
  resendSaveContact: (token, clientId) =>
    axios.post(`${CRM}/clients/${clientId}/automation/resend-save-contact`, null, { headers: authHeaders(token) }).then((r) => r.data),
  sendAddress: (token, clientId) =>
    axios.post(`${CRM}/clients/${clientId}/automation/send-address`, null, { headers: authHeaders(token) }).then((r) => r.data),
  sendWelcome: (token, clientId) =>
    axios.post(`${CRM}/clients/${clientId}/automation/send-welcome`, null, { headers: authHeaders(token) }).then((r) => r.data),
  putCallAnchor: (token, clientId) =>
    axios.post(`${CRM}/clients/${clientId}/automation/call-anchor`, null, { headers: authHeaders(token) }).then((r) => r.data),

  // ----- status marketing -----
  getNativeMarketingOverview: (token) =>
    axios.get(`${CRM}/marketing/native/overview`, { headers: authHeaders(token) }).then((r) => r.data),
  listNativeMarketingCampaigns: (token, params) =>
    axios
      .get(`${CRM}/marketing/native/campaigns`, { params, headers: authHeaders(token) })
      .then((r) => r.data),
  getNativeMarketingCampaign: (token, campaignId) =>
    axios
      .get(`${CRM}/marketing/native/campaigns/${campaignId}`, { headers: authHeaders(token) })
      .then((r) => r.data),
  createNativeMarketingCampaign: (token, body) =>
    axios.post(`${CRM}/marketing/native/campaigns`, body, { headers: authHeaders(token) }).then((r) => r.data),
  patchNativeMarketingCampaign: (token, campaignId, body) =>
    axios
      .patch(`${CRM}/marketing/native/campaigns/${campaignId}`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  archiveNativeMarketingCampaign: (token, campaignId) =>
    axios
      .post(`${CRM}/marketing/native/campaigns/${campaignId}/archive`, null, { headers: authHeaders(token) })
      .then((r) => r.data),
  unarchiveNativeMarketingCampaign: (token, campaignId) =>
    axios
      .post(`${CRM}/marketing/native/campaigns/${campaignId}/unarchive`, null, { headers: authHeaders(token) })
      .then((r) => r.data),
  publishNativeMarketingCampaignWhatsapp: (token, campaignId) =>
    axios
      .post(
        `${CRM}/marketing/native/campaigns/${campaignId}/publish-whatsapp`,
        null,
        { headers: authHeaders(token) }
      )
      .then((r) => r.data),
  createNativeMarketingCampaignSlide: (token, campaignId, formData) =>
    axios
      .post(`${CRM}/marketing/native/campaigns/${campaignId}/slides`, formData, {
        headers: authHeaders(token),
      })
      .then((r) => r.data),
  generateNativeMarketingCampaign: (token, campaignId, formData) =>
    axios
      .post(`${CRM}/marketing/native/campaigns/${campaignId}/generate`, formData, {
        headers: authHeaders(token),
      })
      .then((r) => r.data),
  generateNativeMarketingSlide: (token, slideId, formData) =>
    axios
      .post(`${CRM}/marketing/native/slides/${slideId}/generate`, formData, {
        headers: authHeaders(token),
      })
      .then((r) => r.data),
  selectNativeMarketingSlideVariant: (token, slideId, variantId) =>
    axios
      .post(
        `${CRM}/marketing/native/slides/${slideId}/variants/${variantId}/select`,
        null,
        { headers: authHeaders(token) }
      )
      .then((r) => r.data),
  deleteNativeMarketingSlide: (token, slideId) =>
    axios.delete(`${CRM}/marketing/native/slides/${slideId}`, { headers: authHeaders(token) }),
  listNativeMorningPlans: (token, params) =>
    axios.get(`${CRM}/marketing/native/plans`, { params, headers: authHeaders(token) }).then((r) => r.data),
  createNativeMorningPlan: (token, body) =>
    axios.post(`${CRM}/marketing/native/plans`, body, { headers: authHeaders(token) }).then((r) => r.data),
  patchNativeMorningPlan: (token, planId, body) =>
    axios
      .patch(`${CRM}/marketing/native/plans/${planId}`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  toggleNativeMorningPlan: (token, planId) =>
    axios
      .post(`${CRM}/marketing/native/plans/${planId}/toggle`, null, { headers: authHeaders(token) })
      .then((r) => r.data),
  createNativeMorningPlanItem: (token, planId, body) =>
    axios
      .post(`${CRM}/marketing/native/plans/${planId}/items`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  deleteNativeMorningPlanItem: (token, planId, itemId) =>
    axios.delete(`${CRM}/marketing/native/plans/${planId}/items/${itemId}`, {
      headers: authHeaders(token),
    }),

  getStudioCapabilities: (token) =>
    axios.get(`${CRM}/marketing/studio/capabilities`, { headers: authHeaders(token) }).then((r) => r.data),
  getStudioHome: (token) =>
    axios.get(`${CRM}/marketing/studio/home`, { headers: authHeaders(token) }).then((r) => r.data),
  listStudioCampaigns: (token, params) =>
    axios.get(`${CRM}/marketing/studio/campaigns`, { params, headers: authHeaders(token) }).then((r) => r.data),
  getStudioCalendar: (token, params) =>
    axios.get(`${CRM}/marketing/studio/calendar`, { params, headers: authHeaders(token) }).then((r) => r.data),
  getStudioCampaign: (token, campaignId) =>
    axios.get(`${CRM}/marketing/studio/campaigns/${campaignId}`, { headers: authHeaders(token) }).then((r) => r.data),
  createStudioCampaign: (token, body) =>
    axios.post(`${CRM}/marketing/studio/campaigns`, body, { headers: authHeaders(token) }).then((r) => r.data),
  patchStudioCampaign: (token, campaignId, body) =>
    axios
      .patch(`${CRM}/marketing/studio/campaigns/${campaignId}`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  generateStudioCampaign: (token, campaignId) =>
    axios
      .post(`${CRM}/marketing/studio/campaigns/${campaignId}/generate`, {}, { headers: authHeaders(token) })
      .then((r) => r.data),
  rewriteStudioText: (token, campaignId, body) =>
    axios
      .post(`${CRM}/marketing/studio/campaigns/${campaignId}/rewrite-text`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  regenerateStudioSlide: (token, campaignId, slideId, body = {}) =>
    axios
      .post(
        `${CRM}/marketing/studio/campaigns/${campaignId}/slides/${slideId}/regenerate`,
        body,
        { headers: authHeaders(token) }
      )
      .then((r) => r.data),
  reorderStudioSlides: (token, campaignId, slideIds) =>
    axios
      .post(
        `${CRM}/marketing/studio/campaigns/${campaignId}/slides/reorder`,
        { slide_ids: slideIds },
        { headers: authHeaders(token) }
      )
      .then((r) => r.data),
  uploadStudioSlide: (token, campaignId, formData) =>
    axios
      .post(`${CRM}/marketing/studio/campaigns/${campaignId}/slides/upload`, formData, {
        headers: authHeaders(token),
      })
      .then((r) => r.data),
  deleteStudioSlide: (token, campaignId, slideId) =>
    axios
      .delete(`${CRM}/marketing/studio/campaigns/${campaignId}/slides/${slideId}`, {
        headers: authHeaders(token),
      })
      .then((r) => r.data),
  scheduleStudioCampaign: (token, campaignId, body) =>
    axios
      .post(`${CRM}/marketing/studio/campaigns/${campaignId}/schedule`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  publishStudioCampaign: (token, campaignId, body = {}) =>
    axios
      .post(`${CRM}/marketing/studio/campaigns/${campaignId}/publish`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  retryStudioChannel: (token, campaignId, channel) =>
    axios
      .post(
        `${CRM}/marketing/studio/campaigns/${campaignId}/retry`,
        { channel },
        { headers: authHeaders(token) }
      )
      .then((r) => r.data),
  cloneStudioCampaign: (token, campaignId) =>
    axios
      .post(`${CRM}/marketing/studio/campaigns/${campaignId}/clone`, {}, { headers: authHeaders(token) })
      .then((r) => r.data),
  listStudioTemplates: (token) =>
    axios.get(`${CRM}/marketing/studio/templates`, { headers: authHeaders(token) }).then((r) => r.data),
  createStudioTemplate: (token, body) =>
    axios.post(`${CRM}/marketing/studio/templates`, body, { headers: authHeaders(token) }).then((r) => r.data),
  patchStudioTemplate: (token, templateId, body) =>
    axios
      .patch(`${CRM}/marketing/studio/templates/${templateId}`, body, { headers: authHeaders(token) })
      .then((r) => r.data),
  duplicateStudioTemplate: (token, templateId) =>
    axios
      .post(`${CRM}/marketing/studio/templates/${templateId}/duplicate`, {}, { headers: authHeaders(token) })
      .then((r) => r.data),
  useStudioTemplate: (token, templateId) =>
    axios
      .post(`${CRM}/marketing/studio/templates/${templateId}/use`, {}, { headers: authHeaders(token) })
      .then((r) => r.data),
  deleteStudioTemplate: (token, templateId) =>
    axios.delete(`${CRM}/marketing/studio/templates/${templateId}`, { headers: authHeaders(token) }),

  getMarketingOverview: (token) =>
    axios.get(`${CRM}/marketing/overview`, { headers: authHeaders(token) }).then((r) => r.data),
  listMarketingCampaigns: (token, params) =>
    axios.get(`${CRM}/marketing/campaigns`, { params, headers: authHeaders(token) }).then((r) => r.data),
  listMarketingMorningPlans: (token, params) =>
    axios
      .get(`${CRM}/marketing/morning-plans`, { params, headers: authHeaders(token) })
      .then((r) => r.data),
  getLegacyMarketingCampaignImportPreview: (token, campaignId) =>
    axios
      .get(`${CRM}/marketing/import-preview/campaigns/${campaignId}`, { headers: authHeaders(token) })
      .then((r) => r.data),
  importLegacyMarketingCampaign: (token, campaignId) =>
    axios
      .post(`${CRM}/marketing/import/campaigns/${campaignId}`, null, { headers: authHeaders(token) })
      .then((r) => r.data),
  getLegacyMarketingPlanImportPreview: (token, planId) =>
    axios
      .get(`${CRM}/marketing/import-preview/plans/${planId}`, { headers: authHeaders(token) })
      .then((r) => r.data),
  importLegacyMarketingPlan: (token, planId) =>
    axios
      .post(`${CRM}/marketing/import/plans/${planId}`, null, { headers: authHeaders(token) })
      .then((r) => r.data),
};

/** Универсальный экстрактор ошибки. Возвращает понятный message. */
export function extractCrmError(err, fallback = "Ошибка запроса") {
  const data = err?.response?.data;
  if (data?.detail) {
    if (typeof data.detail === "object" && data.detail !== null) {
      return data.detail.message || data.detail.error || fallback;
    }
    return data.detail;
  }
  if (err?.message) return err.message;
  return fallback;
}

export function extractCrmErrorPayload(err) {
  const data = err?.response?.data;
  if (data?.detail !== undefined && data.detail !== null) {
    return data.detail;
  }
  if (data) return data;
  return { message: extractCrmError(err, "Операция не выполнена") };
}

export function userHasCrmAccess(user) {
  if (!user) return false;
  if (user.is_admin) return true;
  return ["admin", "manager", "cashier"].includes(user.role);
}

export function userCanEditCrm(user) {
  if (!user) return false;
  if (user.is_admin) return true;
  return ["admin", "manager"].includes(user.role);
}

export function userCanManageCrmSettings(user) {
  if (!user) return false;
  return !!user.is_admin || user.role === "admin";
}

export function userCanAccessCrmMarketing(user) {
  if (!user) return false;
  if (user.is_admin) return true;
  return ["admin", "manager", "content_manager"].includes(user.role);
}

export function crmStaticUrl(relativePath, backendUrl = process.env.REACT_APP_BACKEND_URL) {
  const raw = String(relativePath || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const cleaned = raw.replace(/^\/+/, "").replace(/^static\//, "");
  const origin = getBackendOrigin(backendUrl);
  return origin ? `${origin}/static/${cleaned}` : `/static/${cleaned}`;
}

/** Открыть карточку заказа в журнале (оплата, заказ у поставщика). */
export function customerOrderFocusPath(orderId) {
  return `/crm/customer-orders?order_id=${orderId}`;
}

export const CLIENT_STATUSES = [
  { value: "new", label: "Новый" },
  { value: "reviewed", label: "Просмотрен" },
  { value: "in_progress", label: "В работе" },
  { value: "exported", label: "Экспортирован" },
  { value: "deal", label: "Сделка" },
  { value: "ignored", label: "Игнорировать" },
  { value: "blocked", label: "Заблокирован" },
];

export const DEAL_STAGES = [
  { value: "inquiry", label: "Запрос" },
  { value: "consulting", label: "Консультация" },
  { value: "quote_sent", label: "КП отправлено" },
  { value: "paid", label: "Оплачено" },
  { value: "closed", label: "Закрыто" },
  { value: "cancelled", label: "Отменено" },
];

export const TAG_COLORS = [
  { value: "primary", label: "Синий" },
  { value: "secondary", label: "Серый" },
  { value: "success", label: "Зелёный" },
  { value: "warning", label: "Жёлтый" },
  { value: "danger", label: "Красный" },
  { value: "info", label: "Голубой" },
  { value: "dark", label: "Тёмный" },
];

export const TAG_COLOR_HEX = {
  primary: "bg-blue-100 text-blue-800 border-blue-200",
  secondary: "bg-gray-100 text-gray-700 border-gray-200",
  success: "bg-green-100 text-green-800 border-green-200",
  warning: "bg-amber-100 text-amber-800 border-amber-200",
  danger: "bg-red-100 text-red-800 border-red-200",
  info: "bg-cyan-100 text-cyan-800 border-cyan-200",
  dark: "bg-slate-800 text-white border-slate-700",
};

export const CUSTOMER_ORDER_STATUS_OPTIONS = [
  { value: "draft", label: "Черновик" },
  { value: "sent", label: "Отправлен клиенту" },
  { value: "viewed", label: "Просмотрен" },
  { value: "awaiting_confirmation", label: "Ожидает подтверждения" },
  { value: "confirmed", label: "Подтвержден клиентом" },
  { value: "awaiting_payment", label: "Ожидает оплаты" },
  { value: "in_work", label: "Принят в работу" },
  { value: "ordered_from_supplier", label: "Заказан у поставщика" },
  { value: "partially_arrived", label: "Частично поступил" },
  { value: "received", label: "Товар поступил" },
  { value: "ready", label: "Готов к выдаче" },
  { value: "completed", label: "Завершен" },
  { value: "cancelled", label: "Отменен" },
  { value: "declined", label: "Клиент отказался" },
  { value: "needs_revision", label: "Нужна правка" },
];

export const CUSTOMER_ORDER_STATUS_COUNTERS = CUSTOMER_ORDER_STATUS_OPTIONS.filter((item) =>
  [
    "draft",
    "sent",
    "viewed",
    "awaiting_confirmation",
    "confirmed",
    "awaiting_payment",
    "in_work",
    "ordered_from_supplier",
    "partially_arrived",
    "received",
    "ready",
    "completed",
    "declined",
  ].includes(item.value)
);

/** Статусы, требующие ручной проверки менеджером */
export const CUSTOMER_ORDER_MANAGER_ATTENTION_STATUSES = ["partially_arrived"];

export const CUSTOMER_ORDER_PUBLIC_STATUS_LABELS = {
  awaiting_confirmation: "Ожидает подтверждения",
  viewed: "Просмотрен",
  confirmed: "Подтвержден",
  expired: "Срок предложения истёк",
  declined: "Клиент отказался",
  cancelled: "Отменен",
};

export const CUSTOMER_ORDER_SOURCE_OPTIONS = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "store", label: "Магазин" },
  { value: "phone", label: "Телефон" },
  { value: "manual", label: "Вручную" },
  { value: "catalog", label: "Каталог" },
  { value: "other", label: "Другое" },
];

export const CUSTOMER_ORDER_EVENT_LABELS = {
  created: "Заказ создан",
  updated: "Заказ изменён",
  status_changed: "Статус изменён",
  item_added: "Позиция добавлена",
  item_updated: "Позиция изменена",
  item_deleted: "Позиция удалена",
  viewed: "Клиент открыл заказ",
  confirmed_by_client: "Клиент подтвердил заказ",
  declined_by_client: "Клиент отказался",
  price_requested_by_client: "Клиент запросил актуальную цену",
  payment_created: "Оплата создана",
  payment_updated: "Оплата изменена",
  payment_cancelled: "Оплата отменена",
  payment_refunded: "Возврат оплаты",
  order_fully_paid: "Заказ полностью оплачен",
  added_to_supplier_cart: "Позиции добавлены в корзину поставщика",
  supplier_order_created: "Создан заказ у поставщика",
  goods_received: "Товары поступили",
  goods_partially_received: "Товары поступили частично",
  client_notified_ready: "Клиент уведомлён о готовности",
  client_notify_ready_failed: "Не удалось уведомить о готовности",
  client_ready_pickup_reminder_sent: "Автонапоминание о готовности к выдаче",
  order_completed: "Заказ завершён",
};

export const MANUAL_PAYMENT_METHOD_OPTIONS = [
  { value: "cash", label: "Наличные" },
  { value: "card", label: "Карта" },
  { value: "kaspi_transfer", label: "Kaspi перевод" },
];

export const CUSTOMER_PAYMENT_STATUS_LABELS = {
  pending: "Ожидает",
  paid: "Оплачен",
  failed: "Ошибка",
  cancelled: "Отменён",
  refunded: "Возврат",
};

export function getCustomerPaymentMethodLabel(method) {
  return MANUAL_PAYMENT_METHOD_OPTIONS.find((item) => item.value === method)?.label
    || ({ kaspi_invoice: "Kaspi счёт", kaspi_qr: "Kaspi QR" }[method])
    || method
    || "—";
}

export function getCustomerPaymentStatusLabel(status) {
  return CUSTOMER_PAYMENT_STATUS_LABELS[status] || status || "—";
}

export function getCustomerOrderPublicStatusLabel(order) {
  if (!order) return "—";
  if (order.public_status_label) return order.public_status_label;
  return CUSTOMER_ORDER_PUBLIC_STATUS_LABELS[order.public_status] || getCustomerOrderStatusLabel(order.status);
}

export function buildCustomerOrderWhatsAppUrl(orderNumber, message) {
  const phone = (process.env.REACT_APP_WHATSAPP_PHONE || process.env.REACT_APP_STORE_PHONE || "").replace(/\D/g, "");
  if (!phone) return null;
  const text = message || `Здравствуйте! Вопрос по заказу ${orderNumber}.`;
  return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
}

export function getCustomerOrderStatusLabel(status) {
  return CUSTOMER_ORDER_STATUS_OPTIONS.find((item) => item.value === status)?.label || status || "—";
}

export function getCustomerOrderSourceLabel(source) {
  return CUSTOMER_ORDER_SOURCE_OPTIONS.find((item) => item.value === source)?.label || source || "—";
}

export function getCustomerOrderEventLabel(eventType) {
  return CUSTOMER_ORDER_EVENT_LABELS[eventType] || eventType || "Событие";
}

export const ONEC_SYNC_STATUS_LABELS = {
  pending: "Ожидает отправки в 1С",
  synced: "Отправлен в 1С",
  failed: "Ошибка 1С",
  skipped: "Пропущен",
};

export function getOnecSyncStatusLabel(status) {
  return ONEC_SYNC_STATUS_LABELS[status] || status || "—";
}
